'use client';

import { MarketType } from '@velocity-exchange/sdk';
import { UIMarket, matchEnum } from '@velocity-exchange/common';
import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';

/**
 * Hook for switching between different markets and managing market state.
 */
export const useSwitchMarket = () => {
	const router = useRouter();
	const selectedMarket = useVelocityStore((s) => s.selectedMarket);
	const tradePageTab = useVelocityStore((s) => s.tradePageTable.currentTab);
	const setVelocityStore = useVelocityStore((s) => s.set);

	const switchMarket = useCallback(
		({
			marketIndex,
			marketType,
			goToTradePage,
		}: {
			marketIndex: number;
			marketType: MarketType;
			goToTradePage?: boolean;
		}) => {
			const newMarket = new UIMarket(marketIndex, marketType);
			const currentMarket = selectedMarket?.current;

			if (!newMarket || !currentMarket?.marketId) {
				return;
			}

			const currentSearchParams = new URLSearchParams(window.location.search);

			const isCurrentlyOnTradePage =
				window.location.pathname === '/' ||
				window.location.pathname === `/${currentMarket.market?.symbol ?? ''}`;
			const isSwitchingToCurrentMarket = currentMarket.equals(newMarket);

			if (!isSwitchingToCurrentMarket) {
				if (isCurrentlyOnTradePage) {
					// We don't want to use router.push here because that causes a full remount of the page when already on the trade page
					// Instead we just manipulate the history. More info here: https://github.com/vercel/next.js/discussions/50711
					window.history.pushState(
						null,
						'',
						`/${newMarket.market.symbol}${
							currentSearchParams.size > 0
								? `?${currentSearchParams.toString()}`
								: ''
						}`
					);
				} else if (goToTradePage) {
					router.push(
						`/${newMarket.market.symbol}${
							currentSearchParams.size > 0
								? `?${currentSearchParams.toString()}`
								: ''
						}`
					);
				}
			} else if (goToTradePage) {
				router.push(
					`/${newMarket.market.symbol}${
						currentSearchParams.size > 0
							? `?${currentSearchParams.toString()}`
							: ''
					}`
				);
			}

			const newMarketIsSpot = matchEnum(newMarket?.marketType, MarketType.SPOT);
			const currentMarketIsSpot = matchEnum(
				currentMarket?.marketType,
				MarketType.SPOT
			);

			// DPE-1073: Switch tab between positions and balances when switching between perp and spot
			// If the tab was already on something else or the market type didn't change, then don't switch it
			if (
				!currentMarketIsSpot &&
				newMarketIsSpot &&
				tradePageTab === 'positions'
			) {
				setVelocityStore((s) => {
					s.tradePageTable.currentTab = 'balances';
				});
			} else if (
				currentMarketIsSpot &&
				!newMarketIsSpot &&
				tradePageTab === 'balances'
			) {
				setVelocityStore((s) => {
					s.tradePageTable.currentTab = 'positions';
				});
			}

			if (
				currentMarket &&
				currentMarket.marketId &&
				!newMarket.equals(currentMarket)
			) {
				setVelocityStore((s) => {
					s.selectedMarket.current = newMarket;
					s.selectedMarket.name = newMarket.uiSymbols.marketDisplaySymbol;
					s.selectedMarket.marketId = newMarket.marketId;
				});
				// reset the saved limit price only when market actually changes
				setVelocityStore((s) => {
					s.tradeForm.savedLimitPrice = null;
				});
			}
		},
		[selectedMarket, tradePageTab, setVelocityStore, router]
	);

	return { switchMarket };
};
